import { getValidAccessToken } from './googleAuth';
import { InboxItem } from '../contexts/AppContext';

// Gmail API Base URL
const GMAIL_API_BASE = 'https://gmail.googleapis.com/gmail/v1/users/me';

interface GmailHeader {
    name: string;
    value: string;
}

interface GmailMessagePayload {
    headers: GmailHeader[];
    body: {
        data?: string;
    };
    parts?: GmailMessagePayload[];
}

export interface GmailMessage {
    id: string;
    threadId: string;
    labelIds: string[];
    snippet: string;
    payload: GmailMessagePayload;
    internalDate: string;
}

interface GmailListResponse {
    messages?: { id: string; threadId: string }[];
    nextPageToken?: string;
    resultSizeEstimate: number;
}

// Helper to get header value
const getHeader = (headers: GmailHeader[], name: string): string => {
    return headers.find(h => h.name.toLowerCase() === name.toLowerCase())?.value || '';
};

// Map Gmail message to App InboxItem
export const mapGmailMessageToInboxItem = (message: GmailMessage): InboxItem => {
    const subject = getHeader(message.payload.headers, 'subject') || '(No Subject)';
    const from = getHeader(message.payload.headers, 'from');
    // Parse sender name from format "Name <email@example.com>"
    const senderName = from.split('<')[0].trim().replace(/^"|"$/g, '') || from;

    const isUnread = message.labelIds.includes('UNREAD');

    // Determine category based on labels or simple logic (can be enhanced with AI later)
    let category: InboxItem['category'] = 'today';
    if (message.labelIds.includes('IMPORTANT')) {
        category = 'urgent';
    } else if (message.labelIds.includes('PROMOTION') || message.labelIds.includes('SOCIAL')) {
        category = 'bundled';
    }

    return {
        id: message.id, // Use Gmail ID directly
        source: 'gmail',
        sender: senderName,
        subject: subject,
        preview: message.snippet, // Gmail provides a snippet!
        timestamp: new Date(parseInt(message.internalDate)).toISOString(), // Gmail uses ms timestamp
        category: category,
        read: !isUnread,
        priority: 'medium', // Default
    };
};

/**
 * List messages from Gmail
 * @param query Gmail search query (e.g., 'is:inbox')
 * @param maxResults Number of messages to fetch (default 10, max 20 to prevent overload)
 */
export const listGmailMessages = async (query: string = 'is:inbox', maxResults: number = 10): Promise<GmailMessage[]> => {
    // CRITICAL: getValidAccessToken is async, must await it!
    const token = await getValidAccessToken();
    if (!token) {
        console.error('[Gmail] No valid access token available');
        return []; // Return empty instead of throwing to gracefully handle
    }

    // Cap at 30 to balance coverage and performance
    const limitedResults = Math.min(maxResults, 30);

    try {
        // 1. List message IDs
        const listParams = new URLSearchParams({
            q: query,
            maxResults: limitedResults.toString(),
        });

        const listResponse = await fetch(`${GMAIL_API_BASE}/messages?${listParams.toString()}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (!listResponse.ok) {
            const errorBody = await listResponse.text();
            console.error('[Gmail] API List Error:', listResponse.status, errorBody);
            return [];
        }

        const listData: GmailListResponse = await listResponse.json();

        if (!listData.messages || listData.messages.length === 0) {
            console.log('[Gmail] No messages found for query:', query);
            return [];
        }

        // 2. Fetch details for each message (in parallel)
        const messagePromises = listData.messages.map(async (msg) => {
            const msgResponse = await fetch(`${GMAIL_API_BASE}/messages/${msg.id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!msgResponse.ok) return null;
            return msgResponse.json() as Promise<GmailMessage>;
        });

        const messages = (await Promise.all(messagePromises)).filter((m): m is GmailMessage => m !== null);
        return messages;

    } catch (error) {
        console.error('Error fetching Gmail messages:', error);
        throw error;
    }
};
